import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { NATIONALITIES } from "@/lib/countries";
import { IdentityFields } from "@/components/IdentityFields";
import { Loader2, ShoppingBag, Store, Truck } from "lucide-react";

const ROLES = [
  { v: "customer", l: "Shop", icon: ShoppingBag },
  { v: "merchant", l: "Sell", icon: Store },
  { v: "driver", l: "Deliver", icon: Truck },
];

export default function Register() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "customer", sex: "", nationality: "", dob: "", criminal_declared: null, criminal_details: "", attest: false });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const roleHome = (u) => u.role === "merchant" ? "/seller" : u.role === "driver" ? "/driver" : "/account";
  const [nextUrl] = useState(() => new URLSearchParams(window.location.search).get("next"));
  const afterRegister = (u) => (window.PublicKeyCredential ? `/account/biometrics?next=${encodeURIComponent(nextUrl || roleHome(u))}` : roleHome(u));
  useEffect(() => { if (user) navigate(afterRegister(user)); }, [user]);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { const u = await register(form); navigate(afterRegister(u)); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || "Registration failed"); setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] grid place-items-center p-6">
      <div className="w-full max-w-md">
        <h1 className="font-display text-3xl font-bold">Create your account</h1>
        <p className="text-muted-foreground mt-1">Join Jamaica's marketplace for everything.</p>

        <div className="grid grid-cols-3 gap-2 mt-6">
          {ROLES.map((r) => (
            <button key={r.v} onClick={() => setForm((f) => ({ ...f, role: r.v }))} className={`flex flex-col items-center gap-1.5 rounded-xl border-2 p-3 text-sm font-semibold transition-colors ${form.role === r.v ? "border-emerald bg-emerald/5" : "border-border"}`} data-testid={`role-${r.v}`}>
              <r.icon size={20} className="text-emerald" /> {r.l}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-3 mt-4">
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Full name" className="w-full rounded-xl border border-border px-4 py-3 text-sm" data-testid="register-name" />
          <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" type="email" className="w-full rounded-xl border border-border px-4 py-3 text-sm" data-testid="register-email" />
          <input value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Password" type="password" className="w-full rounded-xl border border-border px-4 py-3 text-sm" data-testid="register-password" />
          <div className="grid grid-cols-2 gap-2">
            <select value={form.sex} onChange={(e) => setForm((f) => ({ ...f, sex: e.target.value }))} className="w-full rounded-xl border border-border px-4 py-3 text-sm bg-white" data-testid="register-sex">
              <option value="">Sex</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
            <select value={form.nationality} onChange={(e) => setForm((f) => ({ ...f, nationality: e.target.value }))} className="w-full rounded-xl border border-border px-4 py-3 text-sm bg-white" data-testid="register-nationality">
              <option value="">Nationality</option>
              {NATIONALITIES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <IdentityFields f={form} setF={setForm} testPrefix="register" />
          {error && <p className="text-sm text-coral">{error}</p>}
          <button disabled={loading} className="w-full bg-ink text-white rounded-xl py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="register-submit">
            {loading ? <Loader2 size={18} className="animate-spin" /> : null} Create Account
          </button>
        </form>
        <p className="text-sm text-center mt-6 text-muted-foreground">Already have an account? <Link to="/login" className="text-emerald font-semibold">Sign in</Link></p>
      </div>
    </div>
  );
}
